import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, fmtUSD, fmtPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import KpiCard from "../components/KpiCard";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { toast } from "sonner";

export default function Labor() {
  const [employees, setEmployees] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [sites, setSites] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openEmp, setOpenEmp] = useState(false);
  const [openTs, setOpenTs] = useState(false);
  const [eForm, setEForm] = useState({ name: "", role_classification: "Clinical", employment_type: "salaried", annual_salary: 0, hourly_rate: 0, benefits_load_factor: 25 });
  const [tForm, setTForm] = useState({
    employee_id: "", period_month: new Date().getMonth() + 1, period_year: new Date().getFullYear(),
    overhead_hours: 0, allocations: [{ site_id: "", cta_id: "", hours: 0 }],
  });

  const load = async () => {
    const [e, t, s, c, sum] = await Promise.all([
      api.get("/employees").then((r) => r.data),
      api.get("/timesheets").then((r) => r.data),
      api.get("/sites").then((r) => r.data),
      api.get("/ctas").then((r) => r.data),
      api.get("/labor/summary").then((r) => r.data),
    ]);
    setEmployees(e); setTimesheets(t); setSites(s); setCtas(c); setSummary(sum);
  };
  useEffect(() => { load(); }, []);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";
  const studyName = (id) => ctas.find((c) => c.id === id)?.study_name || "—";
  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const createEmp = async () => {
    if (!eForm.name) return toast.error("Name required");
    await api.post("/employees", {
      ...eForm,
      annual_salary: eForm.employment_type === "salaried" ? Number(eForm.annual_salary) : null,
      hourly_rate: eForm.employment_type === "hourly" ? Number(eForm.hourly_rate) : null,
      benefits_load_factor: Number(eForm.benefits_load_factor),
    });
    toast.success("Employee added"); setOpenEmp(false); load();
  };

  const delEmp = async (id) => { if (window.confirm("Delete employee?")) { await api.delete(`/employees/${id}`); load(); } };

  const createTs = async () => {
    if (!tForm.employee_id) return toast.error("Select employee");
    await api.post("/timesheets", {
      ...tForm,
      period_month: Number(tForm.period_month), period_year: Number(tForm.period_year),
      overhead_hours: Number(tForm.overhead_hours),
      allocations: tForm.allocations.filter((a) => a.site_id).map((a) => ({ ...a, hours: Number(a.hours), cta_id: a.cta_id || null })),
    });
    toast.success("Timesheet saved"); setOpenTs(false); load();
  };

  const laborBySite = (summary?.by_site || []).map((x) => ({ name: siteName(x.site_id), cost: x.cost }));
  const laborByRole = (summary?.by_role || []);

  return (
    <AppShell
      title="Labor & Timesheets"
      subtitle="Tie staff hours to sites and studies for true profitability"
      actions={
        <>
          <Dialog open={openEmp} onOpenChange={setOpenEmp}>
            <DialogTrigger asChild><Button variant="outline" data-testid="new-employee-btn"><Plus className="w-4 h-4 mr-1" /> Employee</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><Label>Name</Label><Input value={eForm.name} onChange={(e) => setEForm({ ...eForm, name: e.target.value })} /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={eForm.role_classification} onValueChange={(v) => setEForm({ ...eForm, role_classification: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["Clinical","Regulatory","Quality","Operations","Admin"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={eForm.employment_type} onValueChange={(v) => setEForm({ ...eForm, employment_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["salaried","hourly"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {eForm.employment_type === "salaried" ? (
                  <div className="col-span-2"><Label>Annual Salary ($)</Label><Input type="number" value={eForm.annual_salary} onChange={(e) => setEForm({ ...eForm, annual_salary: e.target.value })} /></div>
                ) : (
                  <div className="col-span-2"><Label>Hourly Rate ($)</Label><Input type="number" value={eForm.hourly_rate} onChange={(e) => setEForm({ ...eForm, hourly_rate: e.target.value })} /></div>
                )}
                <div className="col-span-2"><Label>Benefits Load Factor (%)</Label><Input type="number" value={eForm.benefits_load_factor} onChange={(e) => setEForm({ ...eForm, benefits_load_factor: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpenEmp(false)}>Cancel</Button><Button className="bg-[#0A4C95] hover:bg-[#083d79]" onClick={createEmp}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openTs} onOpenChange={setOpenTs}>
            <DialogTrigger asChild><Button className="bg-[#F05D23] hover:bg-[#d94e18]" data-testid="new-timesheet-btn"><Plus className="w-4 h-4 mr-1" /> Timesheet</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New Timesheet Entry</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-3 py-2">
                <div>
                  <Label>Employee</Label>
                  <Select value={tForm.employee_id} onValueChange={(v) => setTForm({ ...tForm, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Month (1-12)</Label><Input type="number" value={tForm.period_month} onChange={(e) => setTForm({ ...tForm, period_month: e.target.value })} /></div>
                <div><Label>Year</Label><Input type="number" value={tForm.period_year} onChange={(e) => setTForm({ ...tForm, period_year: e.target.value })} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mt-2">
                  <h4 className="text-sm font-semibold">Allocations</h4>
                  <Button size="sm" variant="outline" onClick={() => setTForm({ ...tForm, allocations: [...tForm.allocations, { site_id: "", cta_id: "", hours: 0 }] })}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2 mt-2">
                  {tForm.allocations.map((a, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <Select value={a.site_id} onValueChange={(v) => { const arr=[...tForm.allocations]; arr[i].site_id = v; setTForm({ ...tForm, allocations: arr }); }}>
                        <SelectTrigger className="col-span-5"><SelectValue placeholder="Site" /></SelectTrigger>
                        <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={a.cta_id} onValueChange={(v) => { const arr=[...tForm.allocations]; arr[i].cta_id = v; setTForm({ ...tForm, allocations: arr }); }}>
                        <SelectTrigger className="col-span-5"><SelectValue placeholder="Study (optional)" /></SelectTrigger>
                        <SelectContent>{ctas.filter(c => !a.site_id || c.site_id === a.site_id).map(c => <SelectItem key={c.id} value={c.id}>{c.study_name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-2" type="number" placeholder="Hours" value={a.hours} onChange={(e) => { const arr=[...tForm.allocations]; arr[i].hours = e.target.value; setTForm({ ...tForm, allocations: arr }); }} />
                    </div>
                  ))}
                </div>
                <div className="mt-3"><Label>Overhead Hours (non-billable)</Label><Input type="number" value={tForm.overhead_hours} onChange={(e) => setTForm({ ...tForm, overhead_hours: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpenTs(false)}>Cancel</Button><Button className="bg-[#0A4C95] hover:bg-[#083d79]" onClick={createTs}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard label="Total Hours" value={Number(summary.total_hours).toLocaleString()} />
          <KpiCard label="Billable Hours" value={Number(summary.billable_hours).toLocaleString()} />
          <KpiCard label="Utilization" value={fmtPct(summary.utilization_rate)} accent="#0A4C95" />
          <KpiCard label="Active Employees" value={employees.length} />
        </div>
      )}

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="tab-timesheets">Timesheets</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-labor-reports">Labor Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
          <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
            <table className="w-full text-sm stryde-table">
              <thead><tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Role</th><th className="text-left px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Base</th><th className="text-right px-4 py-2.5">Effective $/hr</th><th></th></tr></thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-semibold">{e.name}</td>
                    <td className="px-4 py-2.5"><Badge variant="outline">{e.role_classification}</Badge></td>
                    <td className="px-4 py-2.5 capitalize">{e.employment_type}</td>
                    <td className="px-4 py-2.5 text-right font-mono-fin">{e.employment_type === "salaried" ? `${fmtUSD(e.annual_salary)}/yr` : `$${Number(e.hourly_rate).toFixed(2)}/hr`}</td>
                    <td className="px-4 py-2.5 text-right font-mono-fin font-semibold">${e.effective_hourly_cost.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right"><Button size="icon" variant="ghost" onClick={() => delEmp(e.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="timesheets">
          <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
            <table className="w-full text-sm stryde-table">
              <thead><tr><th className="text-left px-4 py-2.5">Employee</th><th className="text-left px-4 py-2.5">Period</th><th className="text-left px-4 py-2.5">Allocations</th><th className="text-right px-4 py-2.5">Total Hours</th></tr></thead>
              <tbody>
                {timesheets.map((t) => {
                  const total = t.allocations.reduce((s, a) => s + a.hours, 0) + (t.overhead_hours || 0);
                  return (
                    <tr key={t.id} className="border-t border-slate-100">
                      <td className="px-4 py-2.5 font-semibold">{empName(t.employee_id)}</td>
                      <td className="px-4 py-2.5">{t.period_month}/{t.period_year}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        {t.allocations.map((a, i) => (
                          <div key={i}>{siteName(a.site_id)} · {studyName(a.cta_id)} · {a.hours}h</div>
                        ))}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono-fin font-semibold">{total}h</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="reports">
          {summary && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Labor Cost by Site</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={laborBySite}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Bar dataKey="cost" fill="#0A4C95" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Labor Cost by Role</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={laborByRole}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="role" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Bar dataKey="cost" fill="#F05D23" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
